import React, { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAppSelector } from "../../../app/hooks";
import { EventType } from "../../../api/events";
import { formatDateToDdMmYyyy, toUtcDate } from "../../../lib/utils";

import { selectEventByTypeAndEntityId } from "../../timeline/logic/slice";
import { selectAllCaseAudits } from "../logic/slice";

const DetectionWindowSummaryHeader: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();

  const occurrenceEvent = useAppSelector(
    caseId
      ? selectEventByTypeAndEntityId(EventType.DATE_OF_OCCURENCE, caseId)
      : () => undefined,
  );

  const detectionEvent = useAppSelector(
    caseId
      ? selectEventByTypeAndEntityId(EventType.DATE_OF_DETECTION, caseId)
      : () => undefined,
  );

  const audits = useAppSelector(selectAllCaseAudits);
  const occurrenceDate = occurrenceEvent?.date;
  const detectionDate = detectionEvent?.date;
  const NO_DATE_FILLED_YET_PLACEHOLDER = "--"

  const auditsInWindow = useMemo(() => {
    if (!occurrenceDate || !detectionDate) return NO_DATE_FILLED_YET_PLACEHOLDER;

    const startDate = toUtcDate(occurrenceDate);
    const endDate = toUtcDate(detectionDate);

    if (!startDate || !endDate) return NO_DATE_FILLED_YET_PLACEHOLDER;

    return audits.filter((audit) => {
      const submissionDate = toUtcDate(audit.audit_submission_date);

      return (
        submissionDate != null &&
        submissionDate >= startDate &&
        submissionDate <= endDate
      );
    }).length;
  }, [occurrenceDate, detectionDate, audits]);

  const renderSummaryItem = (label: string, value: string | number) => (
    <div className="flex min-w-0 flex-col gap-1 font-[Inter]">
      <span className="text-[12px] font-medium leading-4 text-[#94A3B8]">
        {label}
      </span>
      <span className="text-[14px] font-medium leading-5 text-[#475569]">
        {value}
      </span>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-col gap-1 font-[Inter]">
        <span className="text-xs font-medium leading-none text-[#0A0A0A]">
          Adverse observations from past audits
        </span>
        <span className="text-[12px] font-normal leading-none text-[#737373]">
          Record whether any past audit or inspection - in hindsight -
          surfaced a signal of suspicion, especially between the Date of Detection
          and the Date of Occurrence.
        </span>
      </div>

      <div className="grid w-full grid-cols-3 rounded-[8px] bg-[#F9FAFB] p-3">
        {renderSummaryItem("Date of Occurrence", occurrenceDate ? formatDateToDdMmYyyy(occurrenceDate) : "--",)}
        {renderSummaryItem("Date of Detection", detectionDate ? formatDateToDdMmYyyy(detectionDate) : "--",)}
        {renderSummaryItem("Audits in this Window", auditsInWindow)}
      </div>
    </div>
  );
};

export default DetectionWindowSummaryHeader;